import React, { useState, useRef, useEffect } from 'react';

function VoiceSelectionModal({
  isOpen,
  onClose,
  voices,
  customVoices = [],
  selectedVoice,
  onSelectVoice,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Stop audio when modal closes
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // Combine and filter voices
  const builtInVoices = voices
    .map((voice) => {
      const voiceObj = typeof voice === 'string' ? { name: voice } : voice;
      return {
        ...voiceObj,
        isBuiltIn: true,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const allVoices = [
    ...customVoices.map((voice) => ({
      ...voice,
      isBuiltIn: false,
    })),
    ...builtInVoices,
  ];

  // Filter voices based on search query (by name and tags)
  const filteredVoices = allVoices.filter((voice) => {
    const searchLower = searchQuery.toLowerCase();

    // Search by name
    if (voice.name.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Search by tags (if voice has tags)
    if (voice.tags && Array.isArray(voice.tags)) {
      return voice.tags.some((tag) =>
        tag.toLowerCase().includes(searchLower)
      );
    }

    return false;
  });

  const handleSelectVoice = (voiceName) => {
    onSelectVoice(voiceName);
    setPlayingVoiceId(null);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    onClose();
  };

  const handlePlayVoice = (e, voiceId, audioUrl) => {
    e.stopPropagation();

    // If clicking the same voice that's currently playing, pause it
    if (isPlaying && playingVoiceId === voiceId) {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
        setPlayingVoiceId(null);
      }
    } else {
      // Stop current audio if any is playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      // Set new audio and play
      setPlayingVoiceId(voiceId);
      setIsPlaying(true);

      // Update audio source and play
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play().catch((error) => {
          console.error('Error playing audio:', error);
          setIsPlaying(false);
        });
      }
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlayingVoiceId(null);
  };

  return (
    <div className="voice-selection-modal-wrapper">
      {/* Header */}
      <div className="voice-selection-card-header">
        <h3 className="voice-selection-card-title">Select a Voice</h3>
        <button
          className="voice-selection-close-btn"
          onClick={onClose}
          title="Close"
        >
          ✕
        </button>
      </div>

      {/* Search Bar */}
      <div className="voice-selection-card-search">
        <input
          type="text"
          placeholder="🔍 Search by name or tag..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="voice-selection-search-input"
          autoFocus
        />
      </div>

      {/* Voices List */}
      <div className="voice-selection-card-list-container">
        {filteredVoices.length === 0 ? (
          <div className="voice-selection-empty">
            <p className="emoji">🎤</p>
            <p>No voices found</p>
          </div>
        ) : (
          <div className="voice-selection-list">
            {filteredVoices.map((voice) => (
              <div
                key={`${voice.isBuiltIn ? 'builtin' : 'custom'}-${voice.id || voice.name}`}
                className="voice-selection-list-item"
                onClick={() => handleSelectVoice(voice.name)}
              >
                {/* Voice Info */}
                <div className="voice-selection-info">
                  {/* Line 1: Name + Badge */}
                  <div className="voice-selection-line-1">
                    <h4>{voice.name}</h4>
                    {voice.isBuiltIn && (
                      <span className="badge builtin-badge">Built-in</span>
                    )}
                    {!voice.isBuiltIn && (
                      <span className="badge custom-badge">Cloned</span>
                    )}
                  </div>

                  {/* Line 2: Tags */}
                  {voice.tags && voice.tags.length > 0 && (
                    <div className="voice-selection-tags">
                      {voice.tags.map((tag, idx) => (
                        <span key={idx} className="tag-inline">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Play/Pause Icon */}
                {voice.audio_url && (
                  <div className="voice-selection-play">
                    <button
                      className={`btn-play-icon ${
                        isPlaying && playingVoiceId === (voice.id || voice.name)
                          ? 'playing'
                          : ''
                      }`}
                      onClick={(e) =>
                        handlePlayVoice(e, voice.id || voice.name, voice.audio_url)
                      }
                      title={
                        isPlaying && playingVoiceId === (voice.id || voice.name)
                          ? 'Pause voice'
                          : 'Play voice'
                      }
                    >
                      {isPlaying && playingVoiceId === (voice.id || voice.name)
                        ? '⏸'
                        : '▶'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hidden Audio Element for playback */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        style={{ display: 'none' }}
      />
    </div>
  );
}

export default VoiceSelectionModal;
